import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  connectAuthEmulator
} from 'firebase/auth';
import {
  getFirestore,
  connectFirestoreEmulator,
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  getDocs,
  where,
  serverTimestamp
} from 'firebase/firestore';
import {
  getFunctions,
  connectFunctionsEmulator,
  httpsCallable
} from 'firebase/functions';

// Firebase configuration - replace with your actual config values
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app);

// Connect to emulators if in development
if (import.meta.env.VITE_FIREBASE_EMULATOR === 'true' && import.meta.env.DEV) {
  try {
    connectAuthEmulator(auth, 'http://localhost:9099');
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectFunctionsEmulator(functions, 'localhost', 5001);
    console.log('🔥 Connected to Firebase emulators');
  } catch (error) {
    console.warn('Firebase emulators already connected or not available');
  }
}

// Configure providers
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

export const facebookProvider = new FacebookAuthProvider();

// Auth methods
export const signInWithGoogle = () => {
  return signInWithPopup(auth, googleProvider);
};

export const signInWithFacebook = () => {
  return signInWithPopup(auth, facebookProvider);
};

export const signInWithEmail = (email, password) => {
  return signInWithEmailAndPassword(auth, email, password);
};

export const signUpWithEmail = async (email, password, displayName) => {
  const result = await createUserWithEmailAndPassword(auth, email, password);
  if (displayName) {
    await updateProfile(result.user, { displayName });
    // Reload user to ensure displayName is updated in the user object
    await result.user.reload();
    // Small delay to ensure Firebase has propagated the profile update
    // before onAuthStateChanged triggers backend sync
    await new Promise(resolve => setTimeout(resolve, 500));
  }
  return result;
};

export const signOut = () => {
  return firebaseSignOut(auth);
};

// Firestore methods
export const submitTestResult = async (userId, testResult) => {
  try {
    const docRef = await addDoc(collection(db, 'testResults'), {
      userId,
      ...testResult,
      completedAt: serverTimestamp()
    });

    // If score is high enough, add to leaderboard
    if (testResult.iqScore >= 120) {
      const user = auth.currentUser;
      await addDoc(collection(db, 'leaderboard'), {
        userId,
        displayName: user.displayName || 'Anonymous',
        photoURL: user.photoURL || null,
        score: testResult.iqScore,
        percentile: testResult.percentile,
        mode: testResult.mode,
        completedAt: serverTimestamp()
      });
    }

    return docRef.id;
  } catch (error) {
    console.error('Error submitting test result:', error);
    throw error;
  }
};

export const getLeaderboard = async (mode = 'comprehensive', limitCount = 50) => {
  try {
    const q = query(
      collection(db, 'leaderboard'),
      where('mode', '==', mode),
      orderBy('score', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    const leaderboard = [];
    snapshot.forEach(doc => {
      leaderboard.push({ id: doc.id, ...doc.data() });
    });

    return leaderboard;
  } catch (error) {
    console.error('Error getting leaderboard:', error);
    throw error;
  }
};

export const getUserResults = async (userId) => {
  try {
    const q = query(
      collection(db, 'testResults'),
      where('userId', '==', userId),
      orderBy('completedAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const results = [];
    snapshot.forEach(doc => {
      results.push({ id: doc.id, ...doc.data() });
    });

    return results;
  } catch (error) {
    console.error('Error getting user results:', error);
    throw error;
  }
};

// Cloud Functions
export const submitTestResultToFunction = httpsCallable(functions, 'submitTestResult');
export const getLeaderboardFromFunction = httpsCallable(functions, 'getLeaderboard');

export default app;